import { useState } from "react";
import { Info } from "lucide-react";

const MAX_LABEL_LENGTH = 60;
const MAX_DESCRIPTION_LENGTH = 300;

export interface PhoneDestinationFormValues {
  phoneNumber: string;
  friendlyName: string;
  description: string;
  enableLiveHandoff?: boolean;
}

type PhoneDestinationVariant = "transfer" | "escalation";

const COPY: Record<
  PhoneDestinationVariant,
  { noun: string; info: string; descriptionLabel: string; descriptionPlaceholder: string; descriptionHint: string }
> = {
  transfer: {
    noun: "Transfer Number",
    info: "Enter the real phone number callers should be transferred to, and describe when the Genie should send them there. Number transfer restrictions vary by country — verify your number is available in your region.",
    descriptionLabel: "When should the Genie transfer callers here?",
    descriptionPlaceholder: "e.g. Callers in Northland or Whangarei, or anyone with a Fisher & Paykel appliance repair",
    descriptionHint: "Leave empty to use this number when a caller asks to speak to a person.",
  },
  escalation: {
    noun: "Escalation Number",
    info: "The number a browser visitor is connected to when they ask to talk to a human. Any valid phone number works. With several numbers, the visitor is connected to the one whose description matches their conversation.",
    descriptionLabel: "When should visitors be connected to this number?",
    descriptionPlaceholder: "e.g. Visitors asking about Auckland builds, or anyone with a warranty claim",
    descriptionHint: "Leave empty to use this number when nothing more specific matches.",
  },
};

interface PhoneDestinationFormProps {
  variant: PhoneDestinationVariant;
  brand?: string;
  initialValues?: PhoneDestinationFormValues;
  /** Offers to switch on Live Handoff from Browser, so website visitors are connected to this number too. */
  offerLiveHandoff?: boolean;
  onSubmit: (values: PhoneDestinationFormValues) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const PhoneDestinationForm = ({
  variant,
  brand = "#4E9CFF",
  initialValues,
  offerLiveHandoff = false,
  onSubmit,
  onCancel,
  isSubmitting,
}: PhoneDestinationFormProps) => {
  const copy = COPY[variant];
  const isEdit = !!initialValues;
  const isPhoneLocked = isEdit && variant === "transfer";
  const [phoneNumber, setPhoneNumber] = useState(initialValues?.phoneNumber ?? "");
  const [friendlyName, setFriendlyName] = useState(initialValues?.friendlyName ?? "");
  const [description, setDescription] = useState(initialValues?.description ?? "");
  const [enableLiveHandoff, setEnableLiveHandoff] = useState(true);

  const handleSubmit = async () => {
    await onSubmit({
      phoneNumber: phoneNumber.trim(),
      friendlyName: friendlyName.trim(),
      description: description.trim(),
      enableLiveHandoff: offerLiveHandoff && enableLiveHandoff,
    });
  };

  const isPhoneValid = isPhoneLocked ||/^\+[1-9]\d{6,14}$/.test(phoneNumber.trim());
  const canSubmit = isPhoneValid && friendlyName.trim().length > 0 && !isSubmitting;
  const ringStyle = { "--tw-ring-color": `${brand}80` } as React.CSSProperties;
  const inputClassName =
    "w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-base sm:text-sm disabled:bg-gray-50 disabled:text-gray-500";

  return (
    <div className="border-t border-gray-200 pt-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        {isEdit ? `Edit ${copy.noun}` : `Add ${copy.noun}`}
      </h3>

      <div className="space-y-4">
        {!isEdit && (
          <div className="rounded-lg p-3" style={{ backgroundColor: `${brand}10`, border: `1px solid ${brand}30` }}>
            <div className="flex gap-2">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: brand }} />
              <p className="text-xs" style={{ color: brand }}>
                {copy.info}
              </p>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
          <input
            type="text"
            className={inputClassName}
            style={ringStyle}
            placeholder="e.g. Auckland office"
            maxLength={MAX_LABEL_LENGTH}
            value={friendlyName}
            onChange={(e) => setFriendlyName(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
          <input
            type="tel"
            className={inputClassName}
            style={ringStyle}
            placeholder="+15551234567"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            disabled={isSubmitting || isPhoneLocked}
          />
          <p className="text-xs text-gray-500 mt-1">
            {isPhoneLocked
              ? `To change the number, remove this one and add a new ${copy.noun.toLowerCase()}.`
              : "Include country code with no spaces (e.g., +64 for New Zealand)"}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{copy.descriptionLabel}</label>
          <textarea
            className={`${inputClassName} resize-y`}
            style={ringStyle}
            rows={3}
            placeholder={copy.descriptionPlaceholder}
            maxLength={MAX_DESCRIPTION_LENGTH}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isSubmitting}
          />
          <div className="flex justify-between gap-3 mt-1">
            <p className="text-xs text-gray-500">{copy.descriptionHint}</p>
            <p className="text-xs text-gray-400 flex-shrink-0">
              {description.length}/{MAX_DESCRIPTION_LENGTH}
            </p>
          </div>
        </div>

        {offerLiveHandoff && (
          <label className="flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 flex-shrink-0"
              style={{ accentColor: brand }}
              checked={enableLiveHandoff}
              onChange={(e) => setEnableLiveHandoff(e.target.checked)}
              disabled={isSubmitting}
            />
            <span>
              <span className="block text-sm font-medium text-gray-700">
                Also connect website visitors to this number
              </span>
              <span className="block text-xs text-gray-500 mt-0.5">
                Switches on Live Handoff from Browser. When a website visitor asks for a person, they get a Connect me
                button that calls your transfer numbers straight from the page.
              </span>
            </span>
          </label>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 min-h-[44px] text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="px-4 py-2 min-h-[44px] text-white rounded-lg transition-opacity disabled:opacity-50 font-medium"
          style={{ backgroundColor: brand }}
          onMouseEnter={(e) => canSubmit && (e.currentTarget.style.opacity = "0.85")}
          onMouseLeave={(e) => canSubmit && (e.currentTarget.style.opacity = "1")}
        >
          {isSubmitting ? "Saving..." : isEdit ? "Save Changes" : `Add ${copy.noun}`}
        </button>
      </div>
    </div>
  );
};

export default PhoneDestinationForm;
